import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CustomTable, ITableHeaderCell } from '../../../../components/CustomTable/table';
import { TransactionRow } from '../../../../components/TransactionRowCommon/TransactionRow';
import { tips } from '../../../../constants/tips';
import { getTransactionsListSelector } from '../../../../ducks/transactions/selector';
import { getTransactionsListThunk } from '../../../../ducks/transactions/thunk';
import { useAppParams } from '../../../../hooks/useAppParams';
import { usePageUpdater } from '../../../../hooks/usePageUpdater';
import { usePagination } from '../../../../hooks/usePagination';
import { useSignalHook } from '../../../../hooks/useSignalHook';
import { ThunkAppDispatch } from '../../../../store';
import { ITransaction } from '../../../../types/transaction';

const headerList: ITableHeaderCell[] = [
  {
    name: 'Transaction ID',
    tooltip: tips.transaction,
  },
  {
    name: 'Amount',
    tooltip: tips.amountAPL,
  },
  {
    name: 'Currency',
  },
  {
    name: 'Fee',
    tooltip: tips.feeAPL,
  },
  {
    name: 'Age',
    tooltip: tips.timestamp,
  },
  {
    name: 'Age (UTC)',
    tooltip: tips.timestampUTC,
  },
  {
    name: 'Sender Wallet',
    tooltip: tips.senderRS,
  },
  {
    name: 'Recipient Wallet',
    tooltip: tips.recipientRS,
  },
  {
    name: 'Transaction type',
    tooltip: tips.transactionType,
  },
];

export const TransactionTable = () => {
  const params = useAppParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { clearSignals, createSignal, removeCurrentSignal } = useSignalHook();

  const dispatch = useDispatch<ThunkAppDispatch>();
  const transactionsList = useSelector(getTransactionsListSelector, shallowEqual);
  const { setNextPage, setPrevPage, page, resetPagination, firstIndex, lastIndex } = usePagination({
    defaultPage: 1,
  });

  const getDataLoader = useCallback(() => {
    if (params.code) return;

    const controller = createSignal();

    dispatch(
      getTransactionsListThunk(
        {
          firstIndex,
          lastIndex,
          account: params?.account,
        },
        {
          signal: controller.signal,
        },
      ),
    )
      .then((res) => {
        if (res !== 'request cancel') {
          setIsLoading(false);
        }
      })
      .finally(() => {
        removeCurrentSignal(controller);
      });
  }, [
    dispatch,
    firstIndex,
    lastIndex,
    params?.account,
    params?.code,
    createSignal,
    removeCurrentSignal,
  ]);

  useEffect(() => {
    resetPagination();
    setIsLoading(true);
  }, [location.pathname]);

  useEffect(() => {
    clearSignals();
  }, [location.pathname, firstIndex, lastIndex, clearSignals]);

  usePageUpdater(getDataLoader);

  if (params.code) return null;

  return (
    <CustomTable<ITransaction>
      tableTitle='All Transactions'
      tableHeaders={headerList}
      data={transactionsList ?? []}
      TableRow={TransactionRow}
      page={page}
      onPaginationNext={setNextPage}
      onPaginationPrev={setPrevPage}
      emptyMessage='No transactions found.'
      isLoading={isLoading}
    />
  );
};
